import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { deleteNote } from 'views/notepad/moduleServices/notepad_services';
import { useEmbeddedNotepadData } from './useEmbeddedNotepadData';
import { useEmbeddedNotepadLinks } from './useEmbeddedNotepadLinks';
import EmbeddedNotepadAccessDenied from './EmbeddedNotepadAccessDenied';
import EmbeddedNotepadNoteSlot from './EmbeddedNotepadNoteSlot';
import EmbeddedNotepadNotePicker from './EmbeddedNotepadNotePicker';
import EmbeddedNotepadNoteDrawer from './EmbeddedNotepadNoteDrawer';
import { EmbeddedNotepadPanelSkeleton } from './EmbeddedNotepadSlotSkeleton';

/**
 * Generic multi-slot embedded Notepad panel (link existing notes to named slots).
 */
const EmbeddedNotepadSlotsPanel = ({
  slots,
  defaultLinks,
  fetchLinks,
  patchLinks,
  onLoadError,
  gridClassName = 'grid grid-cols-1 gap-4 md:grid-cols-2',
  onLinkSuccess,
  onLinkError,
}) => {
  const {
    hasNotepadAccess,
    notes,
    clusters,
    isNotesLoading,
    isNotesLoadingNetwork,
    refreshNotes,
    updateNotesOptimistically,
  } = useEmbeddedNotepadData();

  const {
    links,
    loading: linksLoading,
    loadedOnce: linksLoadedOnce,
    patchLink,
    clearLink,
  } = useEmbeddedNotepadLinks({
    enabled: hasNotepadAccess,
    defaultLinks,
    fetchLinks,
    patchLinks,
    onLoadError,
  });

  const [pickerSlotKey, setPickerSlotKey] = useState(null);
  const [drawerNoteId, setDrawerNoteId] = useState(null);

  const notesById = useMemo(() => {
    const map = new Map();
    notes.forEach((note) => map.set(String(note.id), note));
    return map;
  }, [notes]);

  const drawerNote = useMemo(() => {
    if (!drawerNoteId) return null;
    return notesById.get(String(drawerNoteId)) || null;
  }, [drawerNoteId, notesById]);

  const saveLink = async (slotKey, noteId) => {
    try {
      await patchLink(slotKey, noteId);
      setPickerSlotKey(null);
      toast.success(noteId ? 'Note linked' : 'Link cleared');
      onLinkSuccess?.(slotKey, noteId);
    } catch (err) {
      onLinkError?.(err);
      throw err;
    }
  };

  const handleDeleteNote = async (noteId) => {
    await deleteNote(noteId);
    await refreshNotes();
    const slotsToClear = slots
      .filter((slot) => String(links[slot.key]) === String(noteId))
      .map((slot) => slot.key);
    for (const key of slotsToClear) {
      try {
        await patchLink(key, null);
      } catch {
        // Missing-note UI until user relinks.
      }
    }
    if (String(drawerNoteId) === String(noteId)) {
      setDrawerNoteId(null);
    }
  };

  if (!hasNotepadAccess) {
    return <EmbeddedNotepadAccessDenied />;
  }

  if (linksLoading || !linksLoadedOnce) {
    return <EmbeddedNotepadPanelSkeleton slots={slots} />;
  }

  const activeSlot = slots.find((slot) => slot.key === pickerSlotKey);

  return (
    <>
      <div className={gridClassName}>
        {slots.map((slot) => {
          const noteId = links[slot.key];
          const note = noteId ? notesById.get(String(noteId)) : null;
          const isResolving = !!noteId && !note && (isNotesLoading || isNotesLoadingNetwork);
          return (
            <EmbeddedNotepadNoteSlot
              key={slot.key}
              label={slot.label}
              noteId={noteId}
              note={note}
              isResolving={isResolving}
              onLink={() => setPickerSlotKey(slot.key)}
              onOpenNote={(linkedNote) => setDrawerNoteId(linkedNote.id)}
              onUnlink={() => clearLink(slot.key)}
            />
          );
        })}
      </div>

      <EmbeddedNotepadNotePicker
        isOpen={!!pickerSlotKey}
        onClose={() => setPickerSlotKey(null)}
        notes={notes}
        isLoading={isNotesLoading}
        title={activeSlot ? `Link note — ${activeSlot.label}` : 'Link note'}
        onSelect={(note) => saveLink(pickerSlotKey, String(note.id))}
      />

      <EmbeddedNotepadNoteDrawer
        isOpen={!!drawerNoteId && !!drawerNote}
        onClose={() => setDrawerNoteId(null)}
        editNote={drawerNote}
        clusters={clusters}
        onOptimisticUpdate={updateNotesOptimistically}
        onDelete={handleDeleteNote}
      />
    </>
  );
};

export default EmbeddedNotepadSlotsPanel;
